import type { AppMessages } from '../../types';

export const app: AppMessages = {
  common: {
    loading: 'Laddar...',
    saving: 'Sparar...',
    save: 'Spara',
    back: 'Tillbaka',
    error: 'Fel',
    notSet: 'Ej angivet',
    user: 'Användare',
    system: 'System',
    copied: 'Kopierat!',
    copy: 'Kopiera',
    cancel: 'Avbryt',
    creating: 'Skapar...',
    processing: 'Bearbetar...',
    you: 'Du',
    stars: 'Stars',
    seeAll: 'Visa alla',
    filter: 'Filtrera',
    amount: 'Belopp',
    date: 'Datum',
    details: 'Detaljer',
    mustBeLoggedIn: 'Du måste vara inloggad',
    daysShort: 'd',
    hoursShort: 't',
    minutesShort: 'min',
    justNow: 'Just nu',
    noTitle: 'Ingen titel',
    payment: 'betalning',
    payments: 'betalningar',
  },
  layout: {
    dashboard: 'Instrumentpanel',
    invoices: 'Fakturor',
    ledger: 'Huvudbok',
    withdraws: 'Uttag',
    redirects: 'Omdirigeringar',
    settings: 'Inställningar',
    admin: 'Admin',
    profile: 'Profil',
    logout: 'Logga ut',
    tonConfigured: 'TON konfigurerad',
    configureTon: 'Konfigurera TON',
    tonNotConfigured: 'TON-adress ej konfigurerad',
    active: 'Aktiv',
    walletConnect: 'Anslut wallet',
    walletTitle: 'Anslut din TON-wallet',
    walletSubtitle: 'Ange TON-adressen som ska ta emot dina uttag.',
    walletConnectedTitle: 'Wallet ansluten',
    walletConnectedSubtitle: 'Dina uttag skickas till denna adress.',
    walletCurrentAddress: 'Ansluten adress',
    walletAddressLabel: 'TON-adress',
    walletHint: 'User-friendly (EQ… / UQ…) eller raw (0:…) format accepteras.',
    walletInvalidAddress: 'Ogiltig TON-adress',
    walletUpdate: 'Uppdatera adress',
    walletChangeAddress: 'Byt adress',
    walletDisconnect: 'Koppla från',
    walletDisconnectConfirmTitle: 'Koppla från wallet?',
    walletDisconnectConfirmBody: 'Du kan inte begära uttag förrän du ansluter en ny adress.',
    walletSaved: 'Wallet ansluten',
    walletDisconnected: 'Wallet frånkopplad',
    newPaymentLink: 'Ny betalningslänk',
    openBot: 'Telegram-bot',
    helpCenter: 'Hjälpcenter',
  },
  login: {
    seoTitle: 'Logga in | Telegram Stars betalningsplattform',
    seoDescription: 'Logga in på ditt Telegram Stars Payment Platform-konto',
    signIn: 'Logga in',
    signUp: 'Registrera',
    email: 'E-post',
    password: 'Lösenord',
    displayNameOptional: 'Visningsnamn (valfritt)',
    displayNamePlaceholder: 'Ditt namn',
    emailPlaceholder: 'din@email.com',
    signInButton: 'Logga in',
    signUpButton: 'Registrera',
    signInWithGoogle: 'Logga in med Google',
    or: 'ELLER',
    welcome: 'Välkommen',
    subtitle: 'Logga in eller skapa ett konto för att komma igång',
    minPasswordHint: 'Minst 6 tecken',
    tipTelegram: '💡 Tips: du kan koppla ditt Telegram-konto i inställningarna efter registrering',
    continueWithTelegram: 'Fortsätt med Telegram',
    telegramAuthError: 'Telegram-inloggning misslyckades. Försök igen.',
    featureInstant: 'Omedelbar kontoskapande',
    featureManage: 'Hantera dina Telegram Stars-betalningar',
    featureWithdraw: 'Ta ut dina medel till TON',
    emailRequired: 'Ange din e-post',
    emailInvalid: 'Ogiltigt e-postformat',
    passwordRequired: 'Ange ditt lösenord',
    passwordRequiredSignup: 'Ange ett lösenord',
    passwordMinLength: 'Lösenordet måste innehålla minst 6 tecken',
    genericError: 'Ett fel uppstod',
    recaptchaFailed: 'reCAPTCHA-verifiering misslyckades. Försök igen.',
    invalidCredentials: 'Fel e-post eller lösenord. Om du inte har ett konto än, skapa ett i fliken «Registrera».',
    wrongPassword: 'Fel lösenord. Kontrollera ditt lösenord.',
    tooManyAttempts: 'För många försök. Försök igen senare.',
    accountDisabled: 'Detta konto har inaktiverats. Kontakta support.',
    authNotEnabled: 'E-post/lösenord-autentisering är inte aktiverad. Kontrollera Firebase-konfigurationen.',
    emailInUse: 'Denna e-post används redan. Logga in med denna e-post.',
    googleAuthError: 'Ett fel uppstod vid Google-autentisering',
    popupClosed: 'Popup-fönstret stängdes. Försök igen.',
    popupBlocked: 'Popup-fönstret blockerades. Tillåt popups och försök igen.',
    popupCancelled: 'Endast en popup-förfrågan tillåts åt gången.',
  },
  dashboard: {
    loading: 'Laddar instrumentpanel...',
    overallSummary: 'Översikt',
    lastMonth: 'Förra månaden',
    lastWeek: 'Förra veckan',
    lastYear: 'Förra året',
    balance: 'Saldo',
    available: 'Tillgängligt',
    totalEarned: 'Totalt intjänat',
    fromLastMonth: 'jämfört med förra månaden',
    recentPayments: 'Senaste betalningar',
    last7days: 'Senaste 7 dagarna',
    last30days: 'Senaste 30 dagarna',
    quickActions: 'Snabbåtgärder',
    createLink: 'Skapa länk',
    createLinkDesc: 'Generera en ny betalningslänk',
    withdraw: 'Ta ut',
    withdrawDesc: 'Ta ut dina Stars till TON',
    invoices: 'Fakturor',
    invoicesDesc: 'Visa betalningshistorik',
    ledger: 'Huvudbok',
    ledgerDesc: 'Visa alla transaktioner',
    recentActivity: 'Senaste aktivitet',
    noRecentActivity: 'Ingen senaste aktivitet',
    mostRecent: 'Senaste',
    noRecentInvoices: 'Inga senaste fakturor',
    allInvoices: 'Alla fakturor',
    paymentReceived: 'Betalning mottagen: {title}',
    withdrawalRequested: 'Uttag begärt: {amount} {suffix}',
    transaction: 'Transaktion: {amount} {suffix}',
    invoiceLabel: 'Faktura #{id}',
    failedLoadBalance: 'Kunde inte ladda saldo',
    chartAmount: 'Belopp',
  },
  invoices: {
    loading: 'Laddar...',
    allInvoices: 'Alla fakturor',
    paid: 'Betalda',
    sent: 'Skickade',
    created: 'Skapade',
    expired: 'Utgångna',
    filter: 'Filtrera',
    createInvoice: '+ Skapa faktura',
    noInvoices: 'Inga fakturor hittades',
    relatedTo: 'Relaterad till:',
    paymentMode: '{mode}-betalning',
    copyLink: 'Kopiera länk',
    copyLinkTitle: 'Kopiera fakturalänk',
    linkNotAvailable: 'Fakturalänk ej tillgänglig',
    linkCopied: 'Fakturalänk kopierad till urklipp!',
    copyFailed: 'Kunde inte kopiera länk',
    failedLoad: 'Kunde inte ladda fakturor',
    statusPaid: 'betald',
    statusSent: 'skickad',
    statusCreated: 'skapad',
    statusExpired: 'utgången',
    typeSingleUse: 'engångs',
    typeRecurring: 'återkommande',
  },
  createInvoice: {
    back: 'Tillbaka',
    title: 'Skapa en betalningslänk',
    subtitle: 'Generera en ny betalningslänk på några sekunder',
    successTitle: 'Betalningslänk skapad!',
    successSubtitle: 'Din betalningslänk är redo att delas',
    paymentLink: 'Betalningslänk:',
    integrationTitle: 'Integrera denna länk på din webbplats',
    integrationDesc: 'Vår Telegram-bot guidar dig steg för steg (API, HTML-knapp, omdirigeringar).',
    openTutorial: 'Öppna handledning',
    testLink: 'Testa länk',
    viewInvoices: 'Visa mina fakturor',
    createNew: 'Skapa en ny länk',
    shareTelegram: 'Dela på Telegram',
    paymentMode: 'Betalningsläge',
    modeStars: 'Stars',
    modeEur: 'EUR (€)',
    modeUsd: 'USD ($)',
    amountInteger: 'Belopp (heltal)',
    amountDecimal: 'Belopp (decimal)',
    amountRequired: 'Ange ett belopp',
    amountIntegerError: 'Beloppet måste vara ett heltal större än 0',
    amountNoDecimals: 'Stars-belopp måste vara ett heltal (inga decimaler)',
    amountMustBeInteger: 'Stars-belopp måste vara ett heltal',
    amountGreaterThanZero: 'Beloppet måste vara större än 0',
    titleOptional: 'Titel (valfritt)',
    titlePlaceholder: 'Betalningstitel (t.ex. Betalning för tjänster)',
    descriptionOptional: 'Beskrivning (valfritt)',
    descriptionPlaceholder: 'Detaljerad betalningsbeskrivning...',
    singleUse: 'Engångslänk',
    singleUseTooltip: 'Om aktiverad kan denna länk endast användas en gång. Efter betalning blir den ogiltig.',
    createLink: 'Skapa länk',
    cancel: 'Avbryt',
    invalidAmount: 'Ogiltigt belopp',
    starsMustBeInteger: 'Beloppet måste vara ett heltal för Stars',
    createError: 'Fel vid skapande av faktura',
  },
  ledger: {
    title: 'Huvudbok',
    typeColumn: 'Typ',
    description: 'Fullständig historik över alla dina transaktioner',
    loading: 'Laddar...',
    noEntries: 'Inga transaktioner för tillfället.',
    typePayment: 'Betalning',
    typeFee: 'Avgift',
    typeNetPayment: 'Nettobetalning',
    typeWithdrawal: 'Uttag',
    typeAdjustment: 'Justering',
    amountStars: 'Belopp (Stars)',
    invoiceDetail: 'Faktura: {title} ({amount} Stars)',
    paidBy: 'Betald av: {payer}',
    payerId: '(ID: {id})',
    withdrawDetail: 'Uttag: {amount} Stars (Status: {status})',
    failedLoad: 'Kunde inte ladda huvudbok',
  },
  withdraws: {
    title: 'Mina uttagsförfrågningar',
    newRequest: '+ Ny förfrågan',
    totalLedger: 'Totalt huvudbok',
    available: 'Tillgängligt',
    pendingWithdrawals: 'Väntande uttag',
    hold21days: '21 dagars spärr',
    payoutNote: 'Utbetalningar behandlas manuellt av en admin. En avgift på 3% tillkommer vid uttag.',
    forecastTitle: 'Uttagsprognos',
    loadingForecast: 'Laddar prognos...',
    noForecast: 'Inga kommande uttagsdatum',
    paymentsCount: '({count} betalningar)',
    noRequests: 'Inga uttagsförfrågningar för tillfället.',
    createFirst: 'Skapa din första förfrågan',
    amount: 'Belopp:',
    destination: 'Destination:',
    processedOn: 'Behandlad:',
    externalRef: 'Extern referens:',
    errorLabel: 'Fel:',
    rejectionReason: 'Avvisningsorsak:',
    failedLoad: 'Kunde inte ladda uttag',
    loading: 'Laddar...',
  },
  createWithdraw: {
    title: 'Begär ett uttag',
    balanceSource: 'Saldo (källa: backend-huvudbok)',
    available: 'Tillgängligt: {amount} Stars',
    pending: 'Väntande uttag: {amount} Stars',
    waiting: '21 dagars spärr: {amount} Stars',
    minAndFee: 'Minsta uttag: {min} Stars · Plattformsavgift: 3% vid utbetalning (manuell behandling)',
    amountLabel: 'Belopp (Stars)',
    minPlaceholder: 'Minimum: {min}',
    feeHint: 'Brutto: {gross} Stars · Avgift (3%): {fee} Stars · Du får: {net} Stars',
    useRegistered: 'Använd min registrerade TON-adress',
    registeredAddress: 'Registrerad adress:',
    tonDestination: 'TON destinationsadress',
    tonPlaceholder: 'EQAbc123...',
    tonFormatHint: 'Format: EQ... eller 0:... (base64 eller hex)',
    createRequest: 'Skapa förfrågan',
    cancel: 'Avbryt',
    creating: 'Skapar...',
    insufficientBalance: '⚠️ Otillräckligt saldo för uttag (minimum: {min} Stars)',
    amountMustBeInteger: 'Beloppet måste vara ett positivt heltal',
    createError: 'Fel vid skapande av uttagsförfrågan',
  },
  redirects: {
    title: 'Omdirigeringslänkar',
    description: 'Skapa korta länkar som omdirigerar till en URL du väljer. Att dela länken /r/din-slug skickar besökare till den konfigurerade URL:en.',
    newRedirect: 'Ny omdirigering',
    clicks: 'klick',
    unique: 'unika',
    links: 'länkar',
    modalTitle: 'Ny omdirigering',
    slugLabel: 'Slug (länk-ID)',
    slugRequired: 'Obligatoriskt fält',
    slugPattern: 'Endast bokstäver, siffror, bindestreck och understreck',
    slugMax: 'Max 64 tecken',
    slugReserved: 'Denna slug är reserverad. Välj en annan.',
    slugPlaceholder: 't.ex.: min-sida',
    urlLabel: 'Destinations-URL',
    urlInvalid: 'Ogiltig URL',
    urlHttpOnly: 'Endast http- och https-URL:er accepteras.',
    urlTooLong: 'URL för lång (max 2048 tecken).',
    urlPlaceholder: 'https://example.com/page',
    createButton: 'Skapa omdirigering',
    loading: 'Laddar…',
    empty: 'Inga omdirigeringar. Klicka på «Ny omdirigering» för att skapa en.',
    copyLink: 'Kopiera länk',
    linkCopied: 'Länk kopierad',
    deleteTitle: 'Ta bort denna omdirigering?',
    yes: 'Ja',
    no: 'Nej',
    delete: 'Ta bort',
    totalClicks: 'Totalt klick',
    uniqueClicks: 'Unika klick',
    avgClicks: 'Snitt klick/besökare',
    loadError: 'Fel vid laddning av omdirigeringar',
    createSuccess: 'Omdirigeringslänk skapad',
    createError: 'Fel vid skapande av omdirigering',
    deleteSuccess: 'Omdirigering borttagen',
    deleteError: 'Fel vid borttagning av omdirigering',
  },
  adminWithdraws: {
    title: 'Administration - Uttagsförfrågningar',
    filterByStatus: 'Filtrera efter status:',
    all: 'Alla',
    noRequests: 'Inga förfrågningar för denna status.',
    agent: 'Agent:',
    amount: 'Belopp:',
    destination: 'Destination:',
    processedOn: 'Behandlad:',
    externalRef: 'Ext. ref.:',
    errorLabel: 'Fel:',
    reason: 'Orsak:',
    approve: '✓ Godkänn',
    reject: '✗ Avvisa',
    markPaid: '✓ Markera betald',
    markFailed: '✗ Markera misslyckad',
    approveConfirm: 'Godkänna denna uttagsförfrågan?',
    rejectPrompt: 'Avvisningsorsak:',
    markPaidPrompt: 'Extern utbetalningsreferens (obligatorisk — TON tx-hash, bankref., etc.):',
    markPaidRequired: 'externalRef krävs för att bekräfta en manuell utbetalning.',
    markFailedPrompt: 'Felmeddelande:',
    approveError: 'Fel vid godkännande av uttag',
    rejectError: 'Fel vid avvisning av uttag',
    updateError: 'Fel vid uppdatering',
    failedLoad: 'Kunde inte ladda uttag',
    notAuthenticated: 'Ej autentiserad',
    operationFailed: 'Åtgärden misslyckades',
    loading: 'Laddar...',
  },
  redirectResolve: {
    missingSlug: 'Slug saknas',
    invalidLink: 'Ogiltig länk',
    notFound: 'Länk hittades inte',
    notFoundTitle: 'Länk hittades inte',
    redirecting: 'Omdirigerar...',
    redirectTitle: 'Omdirigering',
  },
  errorBoundary: {
    title: 'Ett fel uppstod',
    subtitle: 'Tyvärr gick något fel. Försök igen.',
    goHome: 'Tillbaka till startsidan',
    reload: 'Ladda om sidan',
    devDetails: 'Felinformation (endast dev):',
  },
};

export const settingsApp = {
  eyebrow: 'INSTÄLLNINGAR',
  profile: 'Profil',
  email: 'E-post',
  telegramId: 'Telegram-ID',
  notLinked: 'Ej kopplad',
  linking: 'Kopplar...',
  linkTelegram: 'Koppla mitt Telegram-konto',
  openFromTelegram: '⚠️ Öppna denna sida från Telegram för att koppla ditt konto',
  displayName: 'Visningsnamn',
  displayNameHint: 'Visningsnamn anges vid kontoskapande',
  withdrawal: 'Uttag',
  tonAddress: 'TON destinationsadress',
  tonAddressPlaceholder: 'EQAbc123... eller 0:...',
  tonAddressHint: 'Denna adress används som standard för dina uttag',
  tonUpdated: 'TON-adress uppdaterad',
  about: 'Om',
  commission: 'Plattformsavgift:',
  commissionValue: '3% på varje betalning',
  minWithdrawal: 'Minsta uttag:',
  minWithdrawalValue: '100 Stars',
  starsCurrency: 'Stars-valuta:',
  starsCurrencyValue: 'XTR',
  telegramWebAppNotAvailable: 'Telegram WebApp ej tillgänglig. Öppna denna sida från Telegram.',
  telegramUserDataNotAvailable: 'Telegram-användardata ej tillgänglig.',
  telegramLinked: 'Telegram-konto kopplat!',
  linkTelegramError: 'Fel vid koppling av Telegram-konto',
  updateError: 'Fel vid uppdatering',
  mustBeLoggedIn: 'Du måste vara inloggad',
};
